
import { useState } from 'react';
import { StateTaxResult } from '../detail';
import masterData from '../../../data/master_data.json';

interface StateTaxVisualizationProps {
  taxResult: StateTaxResult;
  stateName: string;
  stateSlug: string;
  formatCurrency: (amount: number) => string;
}

const getCategoryColor = (category: string): string => {
  const colors: Record<string, string> = {
    'Federal Tax': 'bg-red-500',
    'State Tax': 'bg-orange-500',
    'Social Security': 'bg-amber-500',
    'Medicare': 'bg-rose-400',
  };
  return colors[category] || 'bg-gray-500';
};

const StateTaxVisualization = ({
  taxResult,
  stateName,
  stateSlug,
  formatCurrency,
}: StateTaxVisualizationProps) => {
  const [activeTab, setActiveTab] = useState<'simple' | 'detailed' | 'brackets'>('simple');

  const chartData = [
    { label: 'Net Pay', amount: taxResult.netSalary, color: 'bg-teal-500' },
    ...taxResult.breakdown.map((item) => ({
      label: item.category,
      amount: item.amount,
      color: getCategoryColor(item.category),
    })),
  ];

  const total = taxResult.grossSalary;

  const usa = (masterData as any).countries.usa;
  const federalBrackets = usa.federal_tax?.brackets || [];
  const stateInMaster = usa.states?.[stateSlug];
  const stateBrackets = stateInMaster?.state_tax?.brackets || [];

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Where Your Money Goes in {stateName}
        </h2>

        {/* Bar Chart */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="mb-8">
            <div className="h-16 flex rounded-lg overflow-hidden">
              {chartData.map((item, index) => {
                const percentage = (item.amount / total) * 100;
                if (percentage < 0.5) return null;
                return (
                  <div
                    key={index}
                    className={`${item.color} relative group cursor-pointer transition-all hover:opacity-90`}
                    style={{ width: `${percentage}%` }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                      {percentage.toFixed(1)}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {chartData.map((item, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded ${item.color}`}></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{item.label}</p>
                  <p className="text-xs text-gray-600">{formatCurrency(item.amount)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabbed Breakdown */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-8">
              {(['simple', 'detailed', 'brackets'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === tab
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab === 'simple'
                    ? 'Simple View'
                    : tab === 'detailed'
                    ? 'Detailed Breakdown'
                    : 'Tax Brackets'}
                </button>
              ))}
            </div>
          </div>

          <div className="p-8">
            {activeTab === 'simple' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">
                        Tax Type
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">
                        Rate
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxResult.breakdown.map((item, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                      >
                        <td className="py-3 px-4 text-sm text-gray-900">{item.category}</td>
                        <td className="py-3 px-4 text-sm text-gray-600 text-right">
                          {(item.rate * 100).toFixed(2)}%
                        </td>
                        <td className="py-3 px-4 text-sm font-semibold text-gray-900 text-right">
                          {formatCurrency(item.amount)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-900 text-white font-bold">
                      <td className="py-4 px-4 text-sm">Total Tax</td>
                      <td className="py-4 px-4 text-sm text-right">
                        {(taxResult.effectiveRate * 100).toFixed(2)}%
                      </td>
                      <td className="py-4 px-4 text-sm text-right">
                        {formatCurrency(taxResult.totalTax)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'detailed' && (
              <div className="space-y-4">
                {taxResult.breakdown.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{item.category}</h4>
                      <span className="text-lg font-bold text-gray-900">
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                    {item.details && (
                      <p className="text-sm text-gray-600 pl-4 border-l-2 border-teal-500">
                        {item.details}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'brackets' && (
              <div className="space-y-8">
                {/* Federal Brackets */}
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-4">
                    Federal Tax Brackets
                  </h4>
                  <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded-r-lg mb-4">
                    <p className="text-sm text-gray-700">
                      <strong>Understanding Tax Brackets:</strong> Your income is taxed
                      progressively. Different portions are taxed at different rates —
                      you don&apos;t pay the highest rate on all your income.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {federalBrackets.map((bracket: any, index: number) => {
                      const isActive =
                        taxResult.grossSalary >= bracket.min &&
                        taxResult.grossSalary <= bracket.max;
                      return (
                        <div
                          key={index}
                          className={`p-4 rounded-lg border-2 ${
                            isActive ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-gray-900">
                                {formatCurrency(bracket.min)} -{' '}
                                {bracket.max >= 999999999 ? '∞' : formatCurrency(bracket.max)}
                              </p>
                              <p className="text-sm text-gray-600">
                                Tax Rate: {(bracket.rate * 100).toFixed(1)}%
                              </p>
                            </div>
                            {isActive && (
                              <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                                Your Bracket
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* State Brackets */}
                {stateBrackets.length > 0 && stateBrackets[0].rate > 0 && (
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-4">
                      {stateName} State Tax Brackets
                    </h4>
                    <div className="space-y-3">
                      {stateBrackets.map((bracket: any, index: number) => {
                        const isActive =
                          taxResult.grossSalary >= bracket.min &&
                          taxResult.grossSalary <= bracket.max;
                        return (
                          <div
                            key={index}
                            className={`p-4 rounded-lg border-2 ${
                              isActive ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {formatCurrency(bracket.min)} -{' '}
                                  {bracket.max >= 999999999 ? '∞' : formatCurrency(bracket.max)}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Tax Rate: {(bracket.rate * 100).toFixed(2)}%
                                </p>
                              </div>
                              {isActive && (
                                <span className="px-3 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                                  Your Bracket
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StateTaxVisualization;
